// The one action worth taking from a creator's profile.
//
// The profile is the moment of highest intent — a brand has just decided they
// want this person — and before this it ended in a back link. Everything here
// exists to keep that decision from cooling off: the campaign list is the
// brand's own, already loaded, and the invitation is one click away.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { useMyCampaigns } from "@/hooks/useMarketplaceCampaigns";
import { useInviteCreator } from "@/hooks/useCollaborations";

export default function InviteToCampaignDialog({
  open,
  onOpenChange,
  creatorProfileId,
  creatorName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creatorProfileId: string;
  creatorName: string;
}) {
  const { t } = useTranslation();
  const { campaigns, loading } = useMyCampaigns();
  const { inviteCreator, busy } = useInviteCreator();

  const [campaignId, setCampaignId] = useState("");
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only OPEN campaigns. A draft has no brief for the creator to read, and a
  // finished one has nothing to join.
  const open_ = campaigns.filter((c) => c.status === "OPEN");

  const submit = async () => {
    setError(null);
    try {
      await inviteCreator(campaignId, creatorProfileId, message.trim() || undefined);
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const close = (next: boolean) => {
    if (!next) {
      // Reset on close so reopening does not show the previous result.
      setSent(false);
      setError(null);
      setMessage("");
      setCampaignId("");
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("marketplace.invite.title", { name: creatorName })}</DialogTitle>
          <DialogDescription>{t("marketplace.invite.description")}</DialogDescription>
        </DialogHeader>

        {sent ? (
          <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-control px-3 py-2">
            {t("marketplace.invite.sent", { name: creatorName })}
          </p>
        ) : loading ? (
          <p className="text-sm text-neutral-500">{t("common.loading")}</p>
        ) : open_.length === 0 ? (
          /* The dead end this dialog exists to remove, so it must not become
             one itself: no open campaign means "create one", not "come back". */
          <div className="text-sm text-neutral-600 space-y-3">
            <p>{t("marketplace.invite.noOpenCampaigns")}</p>
            <Link
              to={`/campanas/nueva?creador=${creatorProfileId}`}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-steel-600 hover:underline"
            >
              {t("marketplace.invite.createCampaign")}
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1.5">
                {t("marketplace.invite.campaign")}
              </label>
              <select
                className="w-full px-3 py-2.5 rounded-control border border-neutral-200 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600/40"
                value={campaignId}
                onChange={(e) => setCampaignId(e.target.value)}
              >
                <option value="">{t("marketplace.invite.selectCampaign")}</option>
                {open_.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1.5">
                {t("marketplace.invite.message")}
              </label>
              <textarea
                className="w-full px-3 py-2.5 rounded-control border border-neutral-200 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600/40"
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t("marketplace.invite.messagePlaceholder")}
              />
              {/* The rate is not asked for here. The creator names it when they
                  accept; a number in the message is a signal, not a term. */}
              <p className="text-xs text-neutral-400 mt-1">{t("marketplace.invite.rateHint")}</p>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        )}

        {!sent && open_.length > 0 && (
          <DialogFooter>
            <Button variant="outline" onClick={() => close(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={submit} disabled={!campaignId || busy}>
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {t("marketplace.invite.send")}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
